import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function UserCompliantTillDate() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const rowsPerPage = 8;

  // ================= FETCH DATA =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch("/report/user/score-report");

        const records = res?.approved || [];

        const formatted = records.map((row) => ({
          id: row.id,
          act: row.compliance,
          particular: row.instance,
          title: "",
          description: row.description || "",
          startDate: row.start_date,
          actionDate: row.action_date,
          endDate: row.end_date,
          status: row.status,
          requestDate: row.request_date,
          responseDate: row.response_date,
          userName: row.user_name,
          approversEmail: row.approvers_email || "-",
          bu: row.business_unit || "-",
          department: row.department || "-"
        }));

        setData(formatted);

      } catch (err) {
        console.error("Error fetching user compliant:", err);
        setError("Failed to fetch compliant records.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ================= SEARCH =================
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    const term = searchTerm.toLowerCase();

    return data.filter(
      (item) =>
        item.id?.toString().includes(term) ||
        item.act?.toLowerCase().includes(term) ||
        item.particular?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  // ================= SORT =================
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];

    sortable.sort((a, b) => {
      const valA = (a[sortConfig.key] || "").toString().toLowerCase();
      const valB = (b[sortConfig.key] || "").toString().toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  // ================= COUNTS =================
  const statusCounts = useMemo(() => {
    const counts = {
      approved: 0,
      total: 0,
    };

    filteredData.forEach((item) => {
      counts.total++;

      if (item.status?.toLowerCase() === "approved") {
        counts.approved++;
      }
    });

    return counts;
  }, [filteredData]);

  // ================= PAGINATION =================
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ================= UI =================
  return (
    <div className="general_">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Compliant (Till Date)</div>
      </div>

      <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>
        
        {/* TOGGLE BUTTONS */}
        <div style={{ display: "flex", gap: "10px", marginLeft: "45px" }}>
          <button className="btn gray-btn">
            Compliant
          </button>

          <button
            className="btn blue-btn"
            onClick={() => navigate("/non-compliant_")}
          >
            Non-Compliant
          </button>
        </div>

        {/* RIGHT SECTION */}
        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          
          {/* STATUS COUNT */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{
              background: "#d4edda",
              color: "#155724",
              padding: "6px 12px",
              borderRadius: "20px",
              fontWeight: "600",
              fontSize: "13px"
            }}>
              Approved: {statusCounts.approved}
            </div>

            <div style={{
              background: "#e2e3e5",
              color: "#383d41",
              padding: "6px 12px",
              borderRadius: "20px",
              fontWeight: "600",
              fontSize: "13px"
            }}>
              Total: {statusCounts.total}
            </div>
          </div>

          {/* SEARCH */}
          <div className="search-box">
            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="search-icon" />
          </div>

        </div>
      </div>

      {/* TABLE */}
      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("id")}>Id {getSortIcon("id")}</th>
            <th onClick={() => requestSort("act")}>Compliance {getSortIcon("act")}</th>
            <th>Instance</th>
            <th onClick={() => requestSort("actionDate")}>Action Date {getSortIcon("actionDate")}</th>
            <th>Status</th>
            <th>User</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                Loading records...
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan="6" style={{ textAlign: "center", color: "red" }}>
                {error}
              </td>
            </tr>
          ) : currentRows.length > 0 ? (
            currentRows.map((row, index) => (
              <tr key={index}>
                <td>{row.id}</td>
                <td>{row.act}</td>

                {/* Instance + Tooltip */}
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>{row.particular}</span>

                    {row.description && (
                      <IoIosInformationCircle
                        title={row.description}
                        style={{
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#1A1744"
                        }}
                      />
                    )}
                  </div>
                </td>

                <td>{row.actionDate}</td>

                <td style={{ color: "green", fontWeight: 600 }}>
                  {row.status}
                </td>

                <td>{row.userName}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: "center" }}>
                No compliant records found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          Prev
        </button>

        <span>
          Page {currentPage} of {totalPages}
        </span>

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}